import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { adminGuard } from './core/guard/admin.guard';
import { authGuard } from './core/guard/auth.guard';
import { publicOnlyGuard } from './core/guard/public-only.guard';
import { TitleResolver } from './core/resolvers/title.resolver';

const routes: Routes = [
    {
        path: '',
        redirectTo: 'tabs/home',
        pathMatch: 'full',
    },

    // ============================================
    // ROTAS PÚBLICAS (Apenas não autenticados)
    // ============================================
    {
        path: 'login',
        loadChildren: () =>
            import('./auth/pages/login/login.module').then((m) => m.LoginPageModule),
        canActivate: [publicOnlyGuard], // Impede acesso se já estiver logado
        data: { title: 'Login', pageView: 'PageLogin' },
        resolve: [TitleResolver],
    },
    {
        path: 'cadastrar',
        loadChildren: () =>
            import('./auth/pages/cadastrar/cadastrar.module').then((m) => m.CadastrarPageModule),
        canActivate: [publicOnlyGuard], // Impede acesso se já estiver logado
        data: { title: 'Cadastrar', pageView: 'PageCadastrar' },
        resolve: [TitleResolver],
    },
    {
        path: 'esqueci-senha',
        loadChildren: () =>
            import('./auth/pages/esqueci-senha/esqueci-senha.module').then(
                (m) => m.EsqueciSenhaModule,
            ),
        // Sem guard - permite acesso para recuperação de senha
        data: { title: 'Esqueci senha', pageView: 'PageEsqueciSenha' },
        resolve: [TitleResolver],
    },
    {
        path: 'deletar-conta-aplicativo-rms',
        loadChildren: () =>
            import('./pages/deletar-conta/deletar-conta.module').then(
                (m) => m.DeletarContaPageModule,
            ),
        // SEM guard — página pública exigida pelo Google Play (Data safety)
        data: { title: 'Exclusão de Conta - RMS Corretora', pageView: 'PageDeletarConta' },
        resolve: [TitleResolver],
    },

    // ============================================
    // ROTAS PROTEGIDAS (Requer autenticação)
    // ============================================
    {
        path: '',
        loadChildren: () => import('./pages/tabs/tabs.module').then((m) => m.TabsPageModule),
        canActivate: [authGuard], // ← PROTEGIDO
        data: { title: 'Home', pageView: 'PageHome' },
        resolve: [TitleResolver],
    },
    {
        path: 'welcome',
        loadChildren: () =>
            import('./pages/welcome/welcome.module').then((m) => m.WelcomePageModule),
        data: { title: 'Bem-vindo', pageView: 'PageWelcome' },
        resolve: [TitleResolver],
    },
    {
        path: 'blogdetails/:id',
        loadChildren: () =>
            import('./pages/blogdetails/blogdetails.module').then((m) => m.BlogdetailsPageModule),
        canActivate: [authGuard], // ← PROTEGIDO
        data: { title: 'Blog detalhes', pageView: 'PageBlog' },
        resolve: [TitleResolver],
    },

    {
        path: 'tabelas-details/:id',
        loadChildren: () =>
            import('./pages/tabelas-details/tabelas-details.module').then(
                (m) => m.TabelasDetailsPageModule,
            ),
        canActivate: [authGuard], // ← PROTEGIDO
        data: { title: 'Tabela detalhes', pageView: 'PageTabela' },
        resolve: [TitleResolver],
    },
    {
        path: 'cotacao/:tabelaId',
        loadChildren: () =>
            import('./pages/cotacao/cotacao.module').then((m) => m.CotacaoPageModule),
        canActivate: [authGuard], // ← PROTEGIDO
        data: { title: 'Cotação', pageView: 'PageCotacao' },
        resolve: [TitleResolver],
    },
    {
        path: 'cadastro-proposta',
        loadChildren: () =>
            import('./pages/cadastro-proposta/cadastro-proposta.module').then(
                (m) => m.CadastroPropostaPageModule,
            ),
        canActivate: [authGuard], // ← PROTEGIDO
        data: { title: 'Cadastro de Proposta', pageView: 'PageCadastroProposta' },
        resolve: [TitleResolver],
    },
    {
        path: 'historico-cotacoes',
        loadChildren: () =>
            import('./pages/historico-cotacoes/historico-cotacoes.module').then(
                (m) => m.HistoricoCotacoesPageModule,
            ),
        canActivate: [authGuard], // ← PROTEGIDO
        data: { title: 'Histórico de Cotações', pageView: 'PageHistoricoCotacoes' },
        resolve: [TitleResolver],
    },
    {
        path: 'painel-corretor',
        redirectTo: 'minhas-comissoes',
        pathMatch: 'full',
    },
    {
        path: 'minhas-comissoes',
        loadChildren: () =>
            import('./pages/minhas-comissoes/minhas-comissoes.module').then(
                (m) => m.MinhasComissoesPageModule,
            ),
        canActivate: [authGuard], // ← PROTEGIDO
        data: { title: 'Minhas Comissões', pageView: 'PageMinhasComissoes' },
        resolve: [TitleResolver],
    },
    {
        path: 'promocao-detail/:id',
        loadChildren: () =>
            import('./pages/promocao-detail/promocao-detail.module').then(
                (m) => m.PromocaoDetailPageModule,
            ),
        canActivate: [authGuard], // ← PROTEGIDO
        data: { title: 'Promoções', pageView: 'PagePromocoes' },
        resolve: [TitleResolver],
    },
    {
        path: 'modulos',
        loadChildren: () => import('./modulos/modulos.module').then((m) => m.ModulosModule),
        canActivate: [authGuard], // ← PROTEGIDO
    },
    {
        path: 'pesquisar',
        loadChildren: () =>
            import('./pages/pesquisar/pesquisar.module').then((m) => m.PesquisarPageModule),
        canActivate: [authGuard], // ← PROTEGIDO
        data: { title: 'Pesquisa de tabelas', pageView: 'PagePesquisa' },
        resolve: [TitleResolver],
    },

    {
        path: 'tabelas',
        loadChildren: () =>
            import('./pages/tabelas/tabelas.module').then((m) => m.TabelasPageModule),
        canActivate: [authGuard], // ← PROTEGIDO
        data: { title: 'Tabelas', pageView: 'PageTabelas' },
        resolve: [TitleResolver],
    },
    {
        path: 'tabelas-details',
        loadChildren: () =>
            import('./pages/tabelas-details/tabelas-details.module').then(
                (m) => m.TabelasDetailsPageModule,
            ),
        canActivate: [authGuard], // ← PROTEGIDO
        data: { title: 'Detalhes da Tabela', pageView: 'PageDetalhesTabela' },
        resolve: [TitleResolver],
    },
    {
        path: 'tabelas-categoria',
        loadChildren: () =>
            import('./pages/tabelas-categoria/tabelas-categoria.module').then(
                (m) => m.TabelasCategoriaPageModule,
            ),
        canActivate: [authGuard], // ← PROTEGIDO
        data: { title: 'Categoria', pageView: 'PageCategoria' },
        resolve: [TitleResolver],
    },
    {
        path: 'dados-corretora',
        loadChildren: () =>
            import('./pages/dados-corretora/dados-corretora.module').then(
                (m) => m.DadosCorretoraPageModule,
            ),
        data: { title: 'Dados da Corretora', pageView: 'PageDadosCorretora' },
        resolve: [TitleResolver],
    },

    {
        path: 'admin',
        loadChildren: () => import('./pages/admin/admin.module').then((m) => m.AdminPageModule),
        canActivate: [authGuard, adminGuard], // ← Auth primeiro, depois Admin
    },
    {
        path: 'suporte-rms',
        loadChildren: () =>
            import('./pages/suporte-rms/suporte-rms.module').then((m) => m.SuporteRmsPageModule),
        canActivate: [authGuard], // ← PROTEGIDO
    },
    {
        path: 'configuracoes',
        loadChildren: () =>
            import('./pages/configuracoes/configuracoes.module').then(
                (m) => m.ConfiguracoesPageModule,
            ),
        canActivate: [authGuard], // ← PROTEGIDO
        data: { title: 'Configurações', pageView: 'PageConfiguracoes' },
        resolve: [TitleResolver],
    },
    {
        path: 'pets',
        loadChildren: () => import('./pages/pets/pets.module').then((m) => m.PetsPageModule),
        canActivate: [authGuard],
        data: { title: 'Meu Pet', pageView: 'PagePets' },
        resolve: [TitleResolver],
    },

    // Wildcard para garantir fallback - TAMBÉM PROTEGIDO
    {
        path: '**',
        redirectTo: 'tabs/home',
        canActivate: [authGuard], // ← PROTEGIDO
    },
];

@NgModule({
    imports: [RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules })],
    exports: [RouterModule],
})
export class AppRoutingModule {}
